import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Trophy, Download, RefreshCw, Medal, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { tierLabel } from '@/lib/tiers';
import { toLocalDateISO } from '@/lib/formatEventDate';

const Stat = ({ label, value, sub }) => (
  <div className="rounded-xl border border-slate-100 bg-white p-4">
    <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

const TierBadge = ({ tier }) => {
  const k = (tier || '').toLowerCase();
  // New 5-tier colours + legacy fallbacks
  const colors = {
    open_play: 'bg-slate-100 text-slate-700',
    verified_player: 'bg-[#56dfb4]/15 text-[#0f172a]',
    instructor_starter: 'bg-slate-100 text-slate-700',
    instructor_plus: 'bg-[#56c6df]/15 text-[#0f172a]',
    instructor_unlimited: 'bg-[#b456df]/15 text-[#0f172a]',
    free: 'bg-slate-100 text-slate-700',
    monthly: 'bg-[#56dfb4]/15 text-[#0f172a]',
    annual: 'bg-[#56c6df]/15 text-[#0f172a]',
    lifetime: 'bg-[#df5681]/15 text-[#0f172a]',
  };
  return <Badge className={`rounded-full text-xs ${colors[k] || colors.open_play}`}>{tierLabel(tier)}</Badge>;
};

export default function AdminReferralsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('this_month');

  const load = () => {
    setLoading(true);
    api.get('/admin/referrals')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load referrals'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleExport = () => {
    // Force download via auth-aware api client; the streaming endpoint
    // returns a CSV blob with a proper Content-Disposition header.
    api.get('/admin/referrals/export.csv', { responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `meetformahjong-referrals-${toLocalDateISO()}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('CSV export started');
      })
      .catch(() => toast.error('CSV export failed'));
  };

  if (loading || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Back to Admin</Link>
        <div className="animate-pulse h-32 bg-slate-100 rounded-2xl" />
      </div>
    );
  }

  const rows = tab === 'this_month' ? data.this_month : tab === 'all_time' ? data.all_time : data.silent_users;
  const isSilent = tab === 'silent_users';

  return (
    <div data-testid="admin-referrals-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="admin-referrals-back"><ArrowLeft className="w-4 h-4" /> Back to Admin</Link>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Referrals</h1>
            <p className="text-xs text-slate-500">{data.month_label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" className="rounded-full gap-2" data-testid="admin-referrals-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <Button onClick={handleExport} variant="outline" size="sm" className="rounded-full gap-2 border-[#56dfb4] text-slate-700 hover:bg-[#56dfb4]/10" data-testid="admin-referrals-export"><Download className="w-4 h-4" /> Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Stat label="Refs This Month" value={data.stats.total_referrals_this_month} sub={`${data.stats.unique_referrers_this_month} unique sharers`} />
        <Stat label="Refs Past 30 Days" value={data.stats.total_referrals_30d} />
        <Stat label="Refs All Time" value={data.stats.total_referrals_all_time} sub={`${data.stats.unique_referrers_all_time} unique sharers`} />
        <Stat label="Silent Paid Users" value={data.silent_users.length} sub="never referred anyone" />
        <Stat label="This Month Top" value={data.this_month[0]?.count || 0} sub={data.this_month[0]?.display_name || '—'} />
      </div>

      <Card className="border-slate-100">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { id: 'this_month', label: `Top this month (${data.this_month.length})` },
              { id: 'all_time', label: `All-time (${data.all_time.length})` },
              { id: 'silent_users', label: `Silent paid users (${data.silent_users.length})` },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-1.5 rounded-full text-xs border transition-all ${tab === t.id ? 'bg-[#df5681] text-white border-[#df5681]' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}
                data-testid={`admin-referrals-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">{isSilent ? 'No silent paid users — everyone has shared their code.' : 'No referrals yet.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {!isSilent && <TableHead className="w-12">#</TableHead>}
                    <TableHead>Member</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Tier</TableHead>
                    {!isSilent && <TableHead>Referrals</TableHead>}
                    {!isSilent && <TableHead>Latest Referral</TableHead>}
                    <TableHead>Last Active</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Code</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={r.user_id} data-testid={`admin-ref-row-${r.user_id}`}>
                      {!isSilent && (
                        <TableCell className="font-semibold">
                          {i < 3 ? <Medal className="w-4 h-4" style={{ color: ['#facc15', '#cbd5e1', '#d97706'][i] }} /> : `#${r.rank}`}
                        </TableCell>
                      )}
                      <TableCell className="font-medium">
                        {r.display_name || '—'}
                        {r.is_banned && <Badge className="ml-2 rounded-full bg-red-100 text-red-700 text-[10px]">banned</Badge>}
                        {r.is_test_account && <Badge className="ml-2 rounded-full bg-amber-100 text-amber-700 text-[10px]">tester</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <a href={`mailto:${r.email}`} className="text-[#56c6df] hover:underline inline-flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {r.email}
                        </a>
                      </TableCell>
                      <TableCell><TierBadge tier={r.subscription_tier} /></TableCell>
                      {!isSilent && <TableCell className="font-semibold">{r.count}</TableCell>}
                      {!isSilent && <TableCell className="text-xs text-slate-500">{fmt(r.latest_referral_at)}</TableCell>}
                      <TableCell className="text-xs text-slate-500">{fmt(r.last_active_at)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{fmt(r.joined_at)}</TableCell>
                      <TableCell className="text-xs"><code className="bg-slate-100 px-2 py-0.5 rounded">{r.referral_code}</code></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
